import React from "react";
import { Alert, Drawer, Spin, Typography } from "antd";
import { ValidationResponse } from "../../types/validator";
import { ValidationResult } from "./ValidationResult";

const { Text } = Typography;

export interface ValidatorDrawerProps {
  open: boolean;
  onClose: () => void;
  title: string;
  result: ValidationResponse | null;
  loading: boolean;
  error: string | null;
}

/**
 * Presentational drawer for viewing full mediation Validator results, opened
 * from the "Details" button on SdkViewPage's Validate card.
 * Fetching is owned by the caller (SdkViewPage), which runs the validator
 * on the fly when the SDK view opens and passes the already-fetched
 * result/loading/error down here, so opening this drawer never triggers a
 * new API call. Rendering reuses <ValidationResult/>, the same component
 * that renders the full per-network/adapter breakdown. The summary stats
 * row is suppressed here (showStats=false) because SdkViewPage already
 * renders it inline above the "Details" button that opens this drawer.
 */
export const ValidatorDrawer: React.FC<ValidatorDrawerProps> = ({
  open,
  onClose,
  title,
  result,
  loading,
  error,
}) => {
  return (
    <Drawer
      title={title}
      width={800}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {error && (
        <Alert
          type="error"
          message="Error"
          description={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Validating release...</Text>
          </div>
        </div>
      )}

      {!loading && result && (
        <ValidationResult result={result} showStats={false} />
      )}
    </Drawer>
  );
};
